import type { BaseClient } from '../../http/BaseClient.js';
import type {
  Deployment,
  RegisterDeploymentRequest,
  RegisterDeploymentResponse,
  DeploymentQuota,
  SetDeploymentQuotaRequest,
  OrgUsageResponse,
  DeploymentUsageResponse,
  FederationStatus,
  MeteringPolicy,
  MeteringReconciliation,
} from './types.js';

// Org-scoped private/sovereign deployment management. Backend routes hang off
// the root authed group — see internal/http/routes/federation (RegisterPortal)
// in cloud-agent-backend. The matching machine edge (/federation/enroll,
// /heartbeat) is driven by the appliance itself, not this SDK.
const BASE = '/deployments';

export class DeploymentsClient {
  constructor(private readonly http: BaseClient) {}

  /** List the org's deployments, newest first. */
  list(): Promise<{ deployments: Deployment[] }> {
    return this.http.get(BASE);
  }

  /** Fetch one deployment by id. */
  get(id: string): Promise<{ deployment: Deployment }> {
    return this.http.get(`${BASE}/${encodeURIComponent(id)}`);
  }

  /**
   * Register a new deployment: reserves a quota slot and mints a one-time
   * enrollment voucher. The voucher + install command in the response are shown
   * ONCE (only the voucher hash is stored server-side).
   */
  register(body: RegisterDeploymentRequest): Promise<RegisterDeploymentResponse> {
    return this.http.post(BASE, { body });
  }

  /** Decommission a deployment, freeing its quota slot. */
  decommission(id: string): Promise<{ success: boolean }> {
    return this.http.post(`${BASE}/${encodeURIComponent(id)}/decommission`);
  }

  /**
   * The caller's org deployment-count quota (used vs cap). Org-scoped: a platform
   * admin sending X-Impersonate-Org-Id sees the impersonated org's quota.
   */
  getQuota(): Promise<{ quota: DeploymentQuota }> {
    return this.http.get(`${BASE}/quota`);
  }

  /**
   * Platform-admin only: set an org's deployment cap (null → revert to the
   * platform default). Operates on the explicit orgId via the admin pool, so it
   * is independent of impersonation. Returns the refreshed quota.
   */
  setOrgQuota(orgId: string, body: SetDeploymentQuotaRequest): Promise<{ quota: DeploymentQuota }> {
    return this.http.put(`/admin/orgs/${encodeURIComponent(orgId)}/deployment-quota`, { body });
  }

  /**
   * Permanently remove a DECOMMISSIONED deployment row from the registry
   * (decommission first — a live row is rejected with 409).
   */
  delete(id: string): Promise<{ success: boolean }> {
    return this.http.delete(`${BASE}/${encodeURIComponent(id)}`);
  }

  // ── Consumption reporting (channel plan §4.3/§4.11) ────────────────────────

  /** Org usage roll-up over [from, to) (RFC3339 or YYYY-MM-DD; default 30d). */
  getOrgUsage(params?: { from?: string; to?: string }): Promise<OrgUsageResponse> {
    return this.http.get(`${BASE}/usage`, { query: params });
  }

  /** One deployment's usage period detail (direct or estate-reported). */
  getUsage(id: string, params?: { from?: string; to?: string }): Promise<DeploymentUsageResponse> {
    return this.http.get(`${BASE}/${encodeURIComponent(id)}/usage`, { query: params });
  }

  // ── Box-side federation panel + the offline file pipe (channel plan §4.8) ──

  /** This private box's federation snapshot (503 on the SaaS / Central). */
  getFederationStatus(): Promise<{ federation: FederationStatus }> {
    return this.http.get('/federation/status');
  }

  /**
   * Seal the pending outbox (+ a liveness heartbeat) into the operator-carried
   * export file. The result is the JSON document to download/carry.
   */
  exportFederationFile(): Promise<unknown> {
    return this.http.get('/federation/export');
  }

  /** Apply the authority's response file on the box (acks + license adoption). */
  importFederationResponse(file: unknown): Promise<{ applied: number; refused: number }> {
    return this.http.post('/federation/import-response', { body: file });
  }

  /** Box-initiated retire: queues the signed decommission for the authority. */
  retire(reason?: string): Promise<{ success: boolean }> {
    return this.http.post('/federation/retire', { body: { reason: reason ?? '' } });
  }

  /**
   * Authority-side import of an operator-carried export file; returns the
   * response file (JSON) to carry back to the deployment.
   */
  importFederationFile(file: unknown): Promise<unknown> {
    return this.http.post(`${BASE}/import`, { body: file });
  }

  // ── Model-B metering policy (platform-admin; close-out P2) ─────────────────

  getMeteringPolicy(orgId: string): Promise<{ policy: MeteringPolicy; exists: boolean }> {
    return this.http.get(`/admin/orgs/${encodeURIComponent(orgId)}/metering-policy`);
  }

  setMeteringPolicy(orgId: string, policy: Omit<MeteringPolicy, 'orgId'>): Promise<{ success: boolean }> {
    return this.http.put(`/admin/orgs/${encodeURIComponent(orgId)}/metering-policy`, { body: policy });
  }

  /** Reported-vs-rated totals — the rating worker's health check. */
  getMeteringReconciliation(
    orgId: string,
    params?: { from?: string; to?: string },
  ): Promise<{ reconciliation: MeteringReconciliation }> {
    return this.http.get(`/admin/orgs/${encodeURIComponent(orgId)}/metering-reconciliation`, { query: params });
  }
}
